import { useQueryClient } from '@tanstack/react-query'
import {
  Camera,
  Crop as CropIcon,
  Ellipsis,
  ImagePlus,
  NotebookPen,
  Plus,
  ScanText,
  Star,
  Trash2,
  UtensilsCrossed,
} from 'lucide-react'
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import type { Crop } from '../../shared/crop'
import { MEAL_NAMES, MEALS } from '../../shared/meals'
import { ActionMenu } from '@/components/ActionMenu'
import { Choices } from '@/components/Choice'
import { CropPhoto } from '@/components/CropPhoto'
import { FormError } from '@/components/FormError'
import { PersonSelect } from '@/components/PersonSelect'
import { ShareWith } from '@/components/ShareWith'
import { Steps } from '@/components/Steps'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { api } from '@/lib/api'
import { mergeReading } from '@/lib/cardReading'
import { useCircleId, useFamily } from '@/lib/families'
import { usePageFrame } from '@/lib/frame'
import { PHOTO_TYPES, photoUrl, preparePhoto, saveCrop, type Copies, type PreparedPhoto } from '@/lib/photos'
import {
  readCard,
  uploadPhoto,
  useBook,
  useRecipe,
  type PhotoKind,
  type Recipe,
  type RecipePhoto,
  type RecipeFields,
} from '@/lib/recipes'
import { useTree } from '@/lib/tree'
import { enqueue, worthRetrying } from '@/lib/uploadQueue'
import { useMe } from '@/lib/useMe'
import { audienceBody, audienceFrom, ONLY_ME, useLastShare, type Audience } from '@/lib/spaces'
import { PageLoader } from '@/components/Loader'

const blank: RecipeFields = { title: '', ingredients: '', steps: '', notes: '', meals: [], fromPersonId: null }

export function NewRecipe() {
  const { id = '' } = useParams()
  const book = useBook(id)
  if (book.isPending) return <PageLoader />
  if (book.isError) return <FormError error={book.error} />
  return <RecipeForm familyId={book.data.familyId} book={{ id: book.data.id, title: book.data.title }} />
}

// A recipe in no book: it goes in Recipes on its own, kept for whoever adds it or shared with a space.
export function NewLooseRecipe() {
  return <RecipeForm familyId={useCircleId()} book={null} />
}

export function EditRecipe() {
  const { id = '' } = useParams()
  const recipe = useRecipe(id)
  if (recipe.isPending) return <PageLoader />
  if (recipe.isError) return <FormError error={recipe.error} />
  return <RecipeForm familyId={recipe.data.familyId} book={recipe.data.book} recipe={recipe.data} />
}

type NewPhoto = { key: string; prepared: PreparedPhoto; preview: string; uploaded: boolean; kind: PhotoKind }
// A photo in the form, kept or new: `key` is a kept photo's id or a new photo's preview.
type Shown = { key: string; src: string; kind: PhotoKind; kept: boolean; locked: boolean }

function RecipeForm({
  familyId,
  book,
  recipe,
}: {
  familyId: string
  // The book it goes in or is in; null for none.
  book: { id: string; title: string } | null
  recipe?: Recipe
}) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const tree = useTree(familyId)
  const family = useFamily(familyId)
  const me = useMe()
  // A recipe started from someone's page (?from=<person>) is theirs to begin with.
  const [params] = useSearchParams()
  const from = recipe ? null : params.get('from')
  const up = recipe
    ? { to: `/recipe/${recipe.id}`, label: recipe.title }
    : from
      ? { to: `/person/${from}`, label: tree.data?.people.find((p) => p.id === from)?.name ?? 'their page' }
      : book
        ? { to: `/book/${book.id}`, label: book.title }
        : { to: `/circle/${familyId}/recipes`, label: 'Recipes' }
  usePageFrame(familyId, {
    title: recipe ? 'Edit recipe' : 'Add a recipe',
    back: { to: up.to, label: `Back to ${up.label}` },
  })
  // Where it's shared on its own: only whoever added it, Everyone, or spaces in any of their circles. Asked of a recipe
  // in no book, and on every edit.
  const lastShare = useLastShare()
  const [who, setWho] = useState<Audience>(() =>
    recipe
      ? recipe.sharedWith
        ? audienceFrom(recipe)
        : { everyone: false, spaceIds: recipe.space ? [recipe.space.id] : [] }
      : lastShare.start(ONLY_ME),
  )
  const asksWho = !!recipe || !book
  const [fields, setFields] = useState<RecipeFields>(
    recipe
      ? {
          title: recipe.title,
          ingredients: recipe.ingredients,
          steps: recipe.steps,
          notes: recipe.notes,
          meals: recipe.meals,
          fromPersonId: recipe.from?.id ?? null,
        }
      : { ...blank, fromPersonId: from },
  )
  // Someone asked for in the link who isn't on this circle's tree is left out.
  const stray = !!tree.data && !!fields.fromPersonId && !tree.data.people.some((p) => p.id === fields.fromPersonId)
  const [savedId, setSavedId] = useState(recipe?.id)
  const [photos, setPhotos] = useState<NewPhoto[]>([])
  const [removed, setRemoved] = useState<string[]>([])
  // Kept photos moved between the card and the dish, and the photo picked for the tile (a kept id or a new key).
  const [moved, setMoved] = useState<Record<string, PhotoKind>>({})
  const [hero, setHero] = useState<string | null>(recipe?.hero ?? null)
  // Kept photos cropped here, saved with the rest; new photos carry their crop with them.
  const [crops, setCrops] = useState<Record<string, { crop: Crop | null; copies: Copies; preview: string }>>({})
  const [cropping, setCropping] = useState<Shown | null>(null)
  const [status, setStatus] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [reading, setReading] = useState(false)
  const [readNote, setReadNote] = useState('')
  const [readError, setReadError] = useState('')

  // Previews are local object URLs; free them when this page closes.
  const previews = useRef<string[]>([])
  useEffect(() => () => previews.current.forEach((url) => URL.revokeObjectURL(url)), [])

  const set = (key: keyof RecipeFields) => (e: { target: { value: string } }) =>
    setFields((f) => ({ ...f, [key]: e.target.value }))

  async function addFiles(files: FileList | null, kind: PhotoKind) {
    setError('')
    for (const file of Array.from(files ?? [])) {
      try {
        const prepared = await preparePhoto(file)
        const preview = URL.createObjectURL(prepared.thumb)
        previews.current.push(preview)
        setPhotos((list) => [...list, { key: preview, prepared, preview, uploaded: false, kind }])
      } catch (e) {
        setError((e as Error).message)
      }
    }
  }

  // Photos of the family making it are theirs, and stay on the recipe page; the form is the keeper's own photos.
  const kept = (recipe?.photos ?? []).filter(
    (p): p is RecipePhoto & { kind: PhotoKind } => !removed.includes(p.id) && p.kind !== 'made',
  )
  const shown: Shown[] = [
    ...kept.map((p) => ({
      key: p.id,
      src: crops[p.id]?.preview ?? photoUrl(p.ref, 'thumb'),
      kind: moved[p.id] ?? p.kind,
      kept: true,
      locked: false,
    })),
    ...photos.map((p) => ({ key: p.key, src: p.preview, kind: p.kind, kept: false, locked: p.uploaded })),
  ]
  const cards = shown.filter((p) => p.kind === 'card')
  const dishes = shown.filter((p) => p.kind === 'food')
  // The photo the tile will show: the one picked, else the first of the dish, else the first of the card.
  const onTile = shown.find((p) => p.key === hero) ?? dishes[0] ?? cards[0]

  function move(item: Shown, kind: PhotoKind) {
    if (item.kept) setMoved((m) => ({ ...m, [item.key]: kind }))
    else setPhotos((list) => list.map((p) => (p.key === item.key ? { ...p, kind } : p)))
  }
  // Where the photo being cropped comes from, and how it's cropped so far.
  const cropSource = (item: Shown) =>
    item.kept
      ? {
          source: photoUrl(item.key, 'original'),
          crop: crops[item.key]?.crop ?? kept.find((p) => p.id === item.key)?.crop ?? null,
        }
      : (({ prepared }) => ({ source: prepared.original, crop: prepared.crop ?? null }))(
          photos.find((p) => p.key === item.key)!,
        )
  async function cropped(item: Shown, crop: Crop | null, copies: Copies) {
    const preview = URL.createObjectURL(copies.thumb)
    previews.current.push(preview)
    if (item.kept) setCrops((all) => ({ ...all, [item.key]: { crop, copies, preview } }))
    else
      setPhotos((list) =>
        list.map((p) => (p.key === item.key ? { ...p, preview, prepared: { ...p.prepared, ...copies, crop } } : p)),
      )
  }
  function remove(item: Shown) {
    if (hero === item.key) setHero(null)
    if (item.kept) setRemoved((r) => [...r, item.key])
    else setPhotos((list) => list.filter((p) => p.key !== item.key))
  }

  // "Capture recipe details from image": the card photos are read and the words fill whatever is still empty.
  // Nothing is saved until Save, so every line can be checked against the card first.
  async function capture() {
    setReading(true)
    setReadError('')
    setReadNote('')
    try {
      const images = await Promise.all(
        cards.slice(0, 4).map(async (item) => {
          if (!item.kept) return photos.find((p) => p.key === item.key)!.prepared.display
          if (crops[item.key]) return crops[item.key]!.copies.display
          const ref = kept.find((p) => p.id === item.key)?.ref ?? item.key
          const res = await fetch(photoUrl(ref, 'display'), { credentials: 'same-origin' })
          if (!res.ok) throw new Error('A photo of the card couldn’t be opened. Please try again.')
          return res.blob()
        }),
      )
      const merged = mergeReading(fields, await readCard(familyId, images))
      setFields(merged.fields)
      setReadNote(merged.note)
      if (merged.filled.length && steps && step === 'card') setStep('name')
    } catch (e) {
      setReadError((e as Error).message)
    } finally {
      setReading(false)
    }
  }

  async function save(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      // A kept photo picked for the tile is saved with the recipe; a new one is marked as it uploads.
      const heroKept = kept.some((p) => p.id === hero)
      const body = {
        ...fields,
        fromPersonId: stray ? null : fields.fromPersonId,
        title: fields.title.trim(),
        ...(heroKept ? { heroPhotoId: hero } : hero === null && recipe?.hero ? { heroPhotoId: null } : {}),
      }
      const sharing = asksWho ? audienceBody(who) : {}
      let id = savedId
      if (id) {
        await api(`/recipes/${id}`, { method: 'PATCH', body: { ...body, ...sharing } })
      } else {
        setStatus('Saving the recipe…')
        const to = book ? `/books/${book.id}/recipes` : `/families/${familyId}/recipes`
        id = (await api<{ id: string }>(to, { method: 'POST', body: { ...body, ...sharing } })).id
        setSavedId(id)
        if (asksWho) lastShare.remember(who)
      }
      for (const photoId of removed) await api(`/photos/${photoId}`, { method: 'DELETE' })
      setRemoved([])
      for (const p of kept) {
        if (moved[p.id] && moved[p.id] !== p.kind) {
          await api(`/photos/${p.id}`, { method: 'PATCH', body: { kind: moved[p.id] } })
        }
      }
      for (const [photoId, { crop, copies }] of Object.entries(crops)) {
        if (removed.includes(photoId)) continue
        setStatus('Saving the crop…')
        await saveCrop(`/photos/${photoId}`, crop, copies)
      }
      setCrops({})
      const pending = photos.filter((p) => !p.uploaded)
      for (const [i, photo] of pending.entries()) {
        setStatus(`Uploading photo ${i + 1} of ${pending.length}…`)
        const role = (p: NewPhoto) => ({ kind: p.kind, hero: p.key === hero })
        try {
          await uploadPhoto(id, photo.prepared, role(photo))
        } catch (e) {
          if (!worthRetrying(e)) throw e
          // The signal dropped: keep the rest on this phone and upload them when it's back.
          await enqueue(
            id,
            pending.slice(i).map((p) => ({ prepared: p.prepared, role: role(p) })),
          )
          break
        }
        setPhotos((list) => list.map((p) => (p.key === photo.key ? { ...p, uploaded: true } : p)))
      }
      await queryClient.invalidateQueries({ queryKey: ['recipe', id] })
      if (book) await queryClient.invalidateQueries({ queryKey: ['book', book.id] })
      await queryClient.invalidateQueries({ queryKey: ['books'] })
      // Whose recipe it is lists it on their page.
      await queryClient.invalidateQueries({ queryKey: ['person'] })
      navigate(`/recipe/${id}`, { replace: true })
    } catch (e) {
      // Whatever already saved stays saved; saving again picks up where this stopped.
      setError(`${(e as Error).message} Your recipe is kept here; tap Save to try again.`)
      setStatus('')
      setBusy(false)
    }
  }

  // A new recipe is added a step at a time, the way an album is started: the card, its name, the words, then
  // photos of the dish. Writing one out (?write=1) starts at its name, with no card to photograph. An edit shows
  // everything at once.
  const writing = params.get('write') === '1'
  const steps: Step[] | null = recipe ? null : writing ? ['name', 'words', 'dish'] : ['card', 'name', 'words', 'dish']
  const [step, setStep] = useState<Step>(steps?.[0] ?? 'card')
  const at = steps ? steps.indexOf(step) : -1
  const last = !steps || at === steps.length - 1
  const shows = (s: Step) => !steps || step === s

  function submit(e: FormEvent) {
    if (last) return void save(e)
    e.preventDefault()
    setStep(steps![at + 1]!)
  }

  const canRead = me.data?.cardReading === true && cards.length > 0
  const photoProps = { onTile: onTile?.key, hero, setHero, move, remove, addFiles, crop: setCropping }

  return (
    <form onSubmit={submit} className="flex flex-col gap-8">
      {cropping && (
        <CropPhoto
          open
          onOpenChange={(open) => !open && setCropping(null)}
          {...cropSource(cropping)}
          onSave={(crop, copies) => cropped(cropping, crop, copies)}
        />
      )}
      <header className="flex flex-col gap-1.5">
        <Link to={up.to} className="self-start eyebrow hover:underline max-nav:hidden">
          ← {up.label}
        </Link>
        <h1 className="text-display">{recipe ? 'Edit recipe' : 'Add a recipe'}</h1>
        {!recipe && <p className="mt-1 whisper">Keep the card. Share the recipe.</p>}
      </header>

      {steps && (
        <div className="flex flex-col gap-4">
          <Steps n={steps.length} at={at} />
          <div className="flex flex-col gap-1">
            <p className="eyebrow">
              Step {at + 1} of {steps.length}
            </p>
            <h2 className="text-title">{(writing ? WRITTEN : STEPS)[step].title}</h2>
            <p className="text-muted-foreground">{(writing ? WRITTEN : STEPS)[step].hint}</p>
          </div>
        </div>
      )}

      {readNote && (shows('name') || shows('words')) && (
        <p role="status" className="rounded-2xl border border-dashed p-3 text-sm text-muted-foreground">
          {readNote}
        </p>
      )}

      {shows('card') && (
        <PhotoSection
          kind="card"
          heading={recipe ? 'The recipe card' : null}
          items={cards}
          {...photoProps}
          empty={
            <>
              <span className="grid size-16 place-items-center rounded-full bg-secondary">
                <Camera className="size-7" strokeWidth={1.6} aria-hidden />
              </span>
              <p className="max-w-sm text-muted-foreground">
                Front and back if there’s writing on both. On a computer you can drop the photos here.
              </p>
            </>
          }
          after={
            canRead && (
              <div className="flex flex-col gap-2 rounded-2xl bg-secondary p-4">
                <Button
                  type="button"
                  size="lg"
                  variant="outline"
                  className="self-start bg-background"
                  disabled={reading || busy}
                  onClick={() => void capture()}
                >
                  <ScanText aria-hidden /> {reading ? 'Reading the card…' : 'Capture recipe details from image'}
                </Button>
                <p className="text-sub text-muted-foreground">
                  Fills in the name, ingredients and steps from the card, word for word, for you to check. Nothing is
                  saved until you save.
                </p>
                <FormError error={readError} />
                {readNote && !shows('name') && (
                  <p role="status" className="text-sm text-muted-foreground">
                    {readNote}
                  </p>
                )}
              </div>
            )
          }
        />
      )}

      {shows('name') && (
        <div className="flex flex-col gap-6">
          <div className="grid gap-2">
            <Label htmlFor="title">Recipe name</Label>
            <Input id="title" required maxLength={80} value={fields.title} onChange={set('title')} />
          </div>
          {(tree.data?.people.length ?? 0) > 0 && (
            <div className="grid gap-2">
              <Label htmlFor="from">Whose recipe is it?</Label>
              <PersonSelect
                id="from"
                placeholder="No one in particular"
                none="No one in particular"
                people={tree.data!.people}
                value={stray ? '' : (fields.fromPersonId ?? '')}
                onChange={(v) => setFields((f) => ({ ...f, fromPersonId: v || null }))}
              />
              <p className="text-sub text-muted-foreground">
                It shows on their page. You’re still the one who added it.
              </p>
            </div>
          )}
          <Choices
            legend="What’s it for?"
            hint="Pick any that fit. A cookbook groups its recipes by these."
            value={fields.meals}
            options={MEALS.map((meal) => [meal, MEAL_NAMES[meal].one])}
            onChange={(meals) => setFields((f) => ({ ...f, meals }))}
          />
          {asksWho && family.data && (
            <div className="grid gap-2">
              <p className="text-sm font-medium" id="who-sees">
                Who sees it?
              </p>
              <ShareWith here={familyId} value={who} onChange={setWho} onlyMeHint="Just you, until you share it" />
              <p className="text-sub text-muted-foreground">
                {book
                  ? `Everyone who sees ${book.title} sees it too, while it’s in that book.`
                  : 'Sending it to a book later adds the people the book is shared with.'}
              </p>
            </div>
          )}
        </div>
      )}

      {shows('words') && (
        <div className="flex flex-col gap-6">
          <div className="grid gap-2">
            <Label htmlFor="ingredients">Ingredients</Label>
            <Textarea
              id="ingredients"
              rows={5}
              maxLength={20_000}
              placeholder="One per line"
              value={fields.ingredients}
              onChange={set('ingredients')}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="steps">Steps</Label>
            <Textarea
              id="steps"
              rows={6}
              maxLength={40_000}
              placeholder="One step per line"
              value={fields.steps}
              onChange={set('steps')}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea id="notes" rows={3} maxLength={20_000} value={fields.notes} onChange={set('notes')} />
          </div>
          {recipe && (
            <p className="text-sub text-muted-foreground">
              Typing it out is optional. A photo of the card is enough to keep it.
            </p>
          )}
        </div>
      )}

      {shows('dish') && (
        <PhotoSection
          kind="food"
          heading={recipe ? 'Photos of the dish' : null}
          items={dishes}
          {...photoProps}
          empty={
            <>
              <span className="grid size-16 place-items-center rounded-full bg-secondary">
                <UtensilsCrossed className="size-7" strokeWidth={1.6} aria-hidden />
              </span>
              <p className="max-w-sm text-muted-foreground">
                The dish made and on the table. The first one goes on the recipe’s tile unless you pick another.
              </p>
            </>
          }
        />
      )}

      <FormError error={error} />
      {status && (
        <p role="status" className="text-sm text-muted-foreground">
          {status}
        </p>
      )}
      {/* On a phone the way forward stays at the bottom of the screen, under the thumb, as the form scrolls. */}
      <div className="flex flex-row-reverse flex-wrap justify-between gap-2 max-nav:sticky max-nav:bottom-0 max-nav:z-10 max-nav:-mx-5 max-nav:bg-background/90 max-nav:px-5 max-nav:pt-3 max-nav:pb-[calc(var(--safe-bottom)+0.5rem)] max-nav:backdrop-blur-lg sm:max-nav:-mx-8 sm:max-nav:px-8">
        <Button type="submit" size="lg" disabled={busy || reading} className="max-nav:min-w-36 max-nav:flex-1">
          {last ? 'Save recipe' : 'Next'}
        </Button>
        {steps && at > 0 && (
          <Button type="button" variant="ghost" size="lg" disabled={busy} onClick={() => setStep(steps[at - 1]!)}>
            Back
          </Button>
        )}
      </div>
    </form>
  )
}

type Step = 'card' | 'name' | 'words' | 'dish'

const STEPS: Record<Step, { title: string; hint: string }> = {
  card: { title: 'Photograph the card', hint: 'The card is the recipe. Its handwriting and its stains are kept too.' },
  name: { title: 'What’s it called?', hint: 'Name it the way you all do. Say whose it is, if you know.' },
  words: {
    title: 'Type it out?',
    hint: 'Optional. A photo of the card is enough to keep it, and anyone can cook from the photo.',
  },
  dish: {
    title: 'Photos of the dish?',
    hint: 'Optional. A few of it made and served, kept beside the card like a little gallery.',
  },
}

// Written out, there's no card yet: the words are the recipe, and a photo of the card can join it later from Edit.
const WRITTEN: Record<Step, { title: string; hint: string }> = {
  ...STEPS,
  words: {
    title: 'Write it out',
    hint: 'The ingredients and the steps, the way it’s made. A photo of the card can be added later, from Edit.',
  },
  dish: {
    title: 'Photos of the dish?',
    hint: 'Optional. A few of it made and served, kept with the recipe like a little gallery.',
  },
}

// Words for one kind of photo: what a photo is called, and how to say it moves to the other kind.
const KIND = {
  card: {
    one: 'card photo',
    section: 'Photos of the card',
    moveTo: 'Move to photos of the dish',
    testId: 'choose-photos',
  },
  food: {
    one: 'dish photo',
    section: 'Photos of the dish',
    moveTo: 'Move to the recipe card',
    testId: 'choose-dish-photos',
  },
} as const

// The photos of one kind, each with a menu (use it on the tile, move it to the other kind, remove it), and the ways
// to add more: the camera, the library, or a drop on a computer.
function PhotoSection({
  kind,
  heading,
  items,
  empty,
  after,
  onTile,
  hero,
  setHero,
  move,
  remove,
  addFiles,
  crop,
}: {
  kind: PhotoKind
  heading: string | null
  items: Shown[]
  empty: ReactNode
  after?: ReactNode
  onTile: string | undefined
  hero: string | null
  setHero: (key: string | null) => void
  move: (item: Shown, kind: PhotoKind) => void
  remove: (item: Shown) => void
  addFiles: (files: FileList | null, kind: PhotoKind) => Promise<void>
  crop: (item: Shown) => void
}) {
  const camera = useRef<HTMLInputElement>(null)
  const library = useRef<HTMLInputElement>(null)
  const words = KIND[kind]
  const some = items.length > 0
  const name = (item: Shown, i: number) => `${item.kept ? '' : 'new '}${words.one} ${i + 1}`
  const Name = (item: Shown, i: number) => name(item, i).replace(/^./, (ch) => ch.toUpperCase())
  let kept = 0
  let added = 0

  return (
    <section
      className="flex flex-col gap-4"
      aria-label={words.section}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('Files')) e.preventDefault()
      }}
      onDrop={(e) => {
        if (!e.dataTransfer.types.includes('Files')) return
        e.preventDefault()
        void addFiles(e.dataTransfer.files, kind)
      }}
    >
      {heading && <h2 className="text-title">{heading}</h2>}
      {some ? (
        <ul className="grid grid-cols-3 gap-1.5 sm:grid-cols-4 sm:gap-2">
          {items.map((item) => {
            // Kept and new photos are counted apart, the way they've always been named.
            const i = item.kept ? kept++ : added++
            return (
              <li key={item.key} className="relative">
                <img
                  src={item.src}
                  alt={Name(item, i)}
                  className="aspect-square w-full rounded-xl bg-muted object-cover"
                />
                {onTile === item.key && (
                  <span className="absolute bottom-1.5 left-1.5 flex items-center gap-1 rounded-full bg-background/90 px-2 py-0.5 text-xs font-medium shadow-paper">
                    <Star className="size-3 fill-current" aria-hidden /> On the tile
                  </span>
                )}
                {!item.locked && (
                  <ActionMenu
                    title={`${Name(item, i)}`}
                    align="end"
                    trigger={
                      <Button
                        type="button"
                        size="icon"
                        variant="secondary"
                        className="absolute top-1.5 right-1.5 size-8 rounded-full bg-background/90 shadow-paper"
                        aria-label={`Options for ${name(item, i)}`}
                      >
                        <Ellipsis aria-hidden />
                      </Button>
                    }
                    groups={[
                      [
                        {
                          label: 'Use on the recipe’s tile',
                          icon: Star,
                          current: hero === item.key,
                          onSelect: () => setHero(hero === item.key ? null : item.key),
                        },
                        { label: 'Rotate, crop and color', icon: CropIcon, onSelect: () => crop(item) },
                        {
                          label: words.moveTo,
                          icon: kind === 'card' ? UtensilsCrossed : NotebookPen,
                          onSelect: () => move(item, kind === 'card' ? 'food' : 'card'),
                        },
                      ],
                      [{ label: 'Remove', icon: Trash2, onSelect: () => remove(item) }],
                    ]}
                  />
                )}
              </li>
            )
          })}
          <li>
            <button
              type="button"
              onClick={() => library.current?.click()}
              className="group grid aspect-square w-full place-items-center rounded-xl bg-secondary transition-colors outline-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              <span className="grid size-10 place-items-center rounded-full bg-background shadow-paper transition-transform group-hover:scale-105">
                <Plus className="size-5" aria-hidden />
              </span>
              <span className="sr-only">Add another {words.one}</span>
            </button>
          </li>
        </ul>
      ) : (
        <div className="flex flex-col items-center gap-5 rounded-[2.5rem] border-2 border-dashed border-input px-6 py-12 text-center sm:py-16">
          {empty}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="lg" variant={some ? 'outline' : 'default'} onClick={() => camera.current?.click()}>
          <Camera aria-hidden /> Take a photo
        </Button>
        <Button type="button" size="lg" variant="outline" onClick={() => library.current?.click()}>
          <ImagePlus aria-hidden /> Choose photos
        </Button>
      </div>
      <input
        ref={camera}
        type="file"
        accept={PHOTO_TYPES}
        capture="environment"
        className="hidden"
        aria-hidden
        tabIndex={-1}
        onChange={(e) => {
          void addFiles(e.target.files, kind)
          e.target.value = ''
        }}
      />
      <input
        ref={library}
        data-testid={words.testId}
        type="file"
        accept={PHOTO_TYPES}
        multiple
        className="hidden"
        aria-hidden
        tabIndex={-1}
        onChange={(e) => {
          void addFiles(e.target.files, kind)
          e.target.value = ''
        }}
      />
      {after}
      <p className="text-sub text-muted-foreground">The original photo is kept exactly as taken.</p>
    </section>
  )
}
